import {
  dispatchCmsFormDirty,
  useCmsPageAppearance,
} from "@/context/cms/CmsPageAppearanceContext";
import { useCmsWebsiteLanguages } from "@/hooks/use-cms-website-languages";
import type { SupportedLocale } from "@/i18n";
import type { CmsPageTextColorMode } from "@/lib/cms/cms-theme.constants";
import { FieldPath, FieldValues, useFormContext } from "react-hook-form";

export type CmsPageThemeFormValues = {
  pageBackgroundColorId: string | null;
  pageTextColorMode: CmsPageTextColorMode;
  pageTextColorId: string | null;
  pageFontFamily: string | null;
  pageFontVariant: string | null;
};

function localeBasePath(lang: SupportedLocale, formPrefix?: string) {
  return formPrefix ? `${formPrefix}.${lang}` : lang;
}

/** Lecture / écriture des paramètres visuels page, synchronisés sur toutes les locales du formulaire. */
export function useCmsPageThemeForm(formPrefix?: string) {
  const form = useFormContext<FieldValues>();
  const { websiteLanguages, defaultWebsiteLanguage } = useCmsWebsiteLanguages();
  const { formId: appearanceFormId } = useCmsPageAppearance();

  const refLang = defaultWebsiteLanguage;
  const refBase = localeBasePath(refLang, formPrefix);

  const pageBackgroundColorId = form.watch(
    `${refBase}.pageBackgroundColorId`,
  ) as string | null | undefined;
  const pageTextColorMode =
    (form.watch(`${refBase}.pageTextColorMode`) as
      | CmsPageTextColorMode
      | undefined) ?? "AUTO";
  const pageTextColorId = form.watch(`${refBase}.pageTextColorId`) as
    | string
    | null
    | undefined;
  const pageFontFamily = form.watch(`${refBase}.pageFontFamily`) as
    | string
    | null
    | undefined;
  const pageFontVariant = form.watch(`${refBase}.pageFontVariant`) as
    | string
    | null
    | undefined;

  const setPageTheme = (patch: Partial<CmsPageThemeFormValues>) => {
    for (const lang of websiteLanguages) {
      const base = localeBasePath(lang, formPrefix);
      if ("pageBackgroundColorId" in patch) {
        form.setValue(
          `${base}.pageBackgroundColorId` as FieldPath<FieldValues>,
          patch.pageBackgroundColorId ?? null,
          { shouldDirty: true },
        );
      }
      if ("pageTextColorMode" in patch) {
        form.setValue(
          `${base}.pageTextColorMode` as FieldPath<FieldValues>,
          patch.pageTextColorMode!,
          { shouldDirty: true },
        );
      }
      if ("pageTextColorId" in patch) {
        form.setValue(
          `${base}.pageTextColorId` as FieldPath<FieldValues>,
          patch.pageTextColorId ?? null,
          { shouldDirty: true },
        );
      }
      if ("pageFontFamily" in patch) {
        form.setValue(
          `${base}.pageFontFamily` as FieldPath<FieldValues>,
          patch.pageFontFamily ?? null,
          { shouldDirty: true },
        );
      }
      if ("pageFontVariant" in patch) {
        form.setValue(
          `${base}.pageFontVariant` as FieldPath<FieldValues>,
          patch.pageFontVariant ?? null,
          { shouldDirty: true },
        );
      }
    }
    dispatchCmsFormDirty(appearanceFormId ?? undefined);
  };

  return {
    pageBackgroundColorId,
    pageTextColorMode,
    pageTextColorId,
    pageFontFamily: pageFontFamily ?? null,
    pageFontVariant: pageFontVariant ?? null,
    setPageTheme,
    websiteLanguages,
  };
}
